/**
 * @unscoped — 예약(Reservation) 메뉴 i18n (2026-08-12).
 *
 * project-i18n / calendar-i18n 패턴 일관 (lib/<domain>-i18n.ts graduation).
 *   - fallback chain: lang → en → FALLBACK_LANG(ko) 명시
 *   - {param} 치환 지원
 *   - 전수 non-empty 가드는 본 모듈 test 가 강제
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES = {
  // ===== 메뉴/네비 =====
  'rsv.title': { ko: '예약', en: 'Reservations', ja: '予約' },
  /* ── 화면 머리 (정본 A3 · 2026-09-08) ────────────────────────────── */
  'rsv.head.help_title': { ko: '이 화면의 규칙', en: 'How this screen works', ja: 'この画面のルール' },
  'rsv.head.desc_book': { ko: '회의실 · 장비를 시간으로 잡습니다.', en: 'Book rooms and equipment by time.', ja: '会議室・備品を時間で予約します。' },
  'rsv.head.desc_mine': { ko: '내가 잡은 예약과 낸 신청입니다.', en: 'Your bookings and requests.', ja: '自分の予約と申請です。' },
  'rsv.head.desc_approvals': { ko: '내가 맡은 자원의 예약 신청입니다.', en: 'Requests for resources you manage.', ja: '担当する資源の予約申請です。' },
  'rsv.nav.book': { ko: '예약하기', en: 'Book', ja: '予約する' },
  'rsv.nav.mine': { ko: '나의 예약', en: 'My reservations', ja: 'マイ予約' },
  'rsv.nav.approvals': { ko: '승인처리', en: 'Approvals', ja: '承認処理' },
  'rsv.nav.collapse': { ko: '패널 접기', en: 'Collapse panel', ja: 'パネルを折りたたむ' },

  // ===== 자원 목록 =====
  'rsv.resources.heading': { ko: '예약 가능 목록', en: 'Available resources', ja: '予約可能な一覧' },
  'rsv.resources.empty': { ko: '등록된 예약 자원이 없습니다. 관리자에게 문의하세요.', en: 'No reservable resources yet. Contact your administrator.', ja: '予約可能な資源がありません。管理者にお問い合わせください。' },
  'rsv.resources.err': { ko: '예약 자원을 불러오지 못했습니다.', en: 'Failed to load resources.', ja: '資源の取得に失敗しました。' },
  'rsv.resources.search': { ko: '자원 검색…', en: 'Search resources…', ja: '資源を検索…' },
  'rsv.resource.manager': { ko: '담당', en: 'Manager', ja: '担当' },
  'rsv.resource.capacity': { ko: '수량 {n}', en: 'Qty {n}', ja: '数量 {n}' },
  'rsv.resource.unit_time': { ko: '시간 단위', en: 'By time', ja: '時間単位' },
  'rsv.resource.unit_day': { ko: '일 단위', en: 'By day', ja: '日単位' },
  'rsv.resource.approval': { ko: '승인 필요', en: 'Approval required', ja: '承認必要' },
  'rsv.resource.instant': { ko: '즉시 확정', en: 'Instant', ja: '即時確定' },
  'rsv.resource.select_hint': { ko: '왼쪽에서 예약할 대상을 선택하세요.', en: 'Select a resource on the left.', ja: '左側から予約対象を選択してください。' },

  // ===== 달력/슬롯 =====
  'rsv.cal.prev': { ko: '이전', en: 'Previous', ja: '前へ' },
  'rsv.cal.next': { ko: '다음', en: 'Next', ja: '次へ' },
  'rsv.cal.today': { ko: '오늘', en: 'Today', ja: '今日' },
  'rsv.cal.month': { ko: '월별', en: 'Month', ja: '月別' },
  'rsv.cal.day': { ko: '일별', en: 'Day', ja: '日別' },
  'rsv.cal.loading': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中…' },
  'rsv.cal.remaining': { ko: '잔여 {n}', en: '{n} left', ja: '残り {n}' },
  'rsv.cal.full': { ko: '마감', en: 'Full', ja: '満了' },
  'rsv.cal.closed': { ko: '운영시간 아님', en: 'Closed', ja: '営業時間外' },
  'rsv.cal.day_hint': { ko: '날짜를 클릭하면 그 날의 시간대가 열립니다.', en: 'Click a date to open its time slots.', ja: '日付をクリックするとその日の時間帯が開きます。' },
  'rsv.cal.pick_range': { ko: '대여 시작일과 반납일을 고르세요.', en: 'Pick start and return dates.', ja: '貸出開始日と返却日を選んでください。' },

  // ===== 신청 =====
  'rsv.book.submit': { ko: '예약 신청', en: 'Request', ja: '予約申請' },
  'rsv.book.submitting': { ko: '신청 중…', en: 'Submitting…', ja: '申請中…' },
  'rsv.book.purpose': { ko: '사용 목적', en: 'Purpose', ja: '利用目的' },
  'rsv.book.purpose_ph': { ko: '예: 주간 팀 회의 (선택)', en: 'e.g. Weekly team meeting (optional)', ja: '例: 週次チーム会議（任意）' },
  'rsv.book.quantity': { ko: '수량', en: 'Quantity', ja: '数量' },
  'rsv.book.selected': { ko: '선택: {range}', en: 'Selected: {range}', ja: '選択: {range}' },
  'rsv.book.ok_pending': { ko: '예약을 신청했습니다. 담당자 승인 후 확정됩니다.', en: 'Requested. It will be confirmed after approval.', ja: '予約を申請しました。担当者の承認後に確定します。' },
  'rsv.book.ok_approved': { ko: '예약이 확정되었습니다.', en: 'Your reservation is confirmed.', ja: '予約が確定しました。' },
  'rsv.book.err': { ko: '예약 신청에 실패했습니다.', en: 'Failed to request the reservation.', ja: '予約申請に失敗しました。' },

  // ===== 나의 예약 =====
  'rsv.mine.heading': { ko: '나의 예약', en: 'My reservations', ja: 'マイ予約' },
  'rsv.mine.empty': { ko: '예약 내역이 없습니다.', en: 'No reservations yet.', ja: '予約履歴がありません。' },
  'rsv.mine.err': { ko: '예약 내역을 불러오지 못했습니다.', en: 'Failed to load reservations.', ja: '予約履歴の取得に失敗しました。' },
  'rsv.mine.cancel': { ko: '신청취소', en: 'Cancel', ja: '申請取消' },
  'rsv.mine.cancel_confirm': { ko: '이 예약을 취소할까요?', en: 'Cancel this reservation?', ja: 'この予約を取り消しますか？' },
  'rsv.mine.cancel_err': { ko: '취소에 실패했습니다.', en: 'Failed to cancel.', ja: '取消に失敗しました。' },
  'rsv.mine.filter_all': { ko: '전체', en: 'All', ja: 'すべて' },
  'rsv.mine.more': { ko: '더 보기', en: 'Load more', ja: 'もっと見る' },

  // ===== 승인처리 =====
  'rsv.approve.heading': { ko: '승인처리', en: 'Approvals', ja: '承認処理' },
  'rsv.approve.empty': { ko: '대기 중인 예약이 없습니다.', en: 'No pending reservations.', ja: '承認待ちの予約はありません。' },
  'rsv.approve.err': { ko: '승인 목록을 불러오지 못했습니다.', en: 'Failed to load approvals.', ja: '承認一覧の取得に失敗しました。' },
  'rsv.approve.approve': { ko: '승인', en: 'Approve', ja: '承認' },
  'rsv.approve.reject': { ko: '반려', en: 'Reject', ja: '却下' },
  'rsv.approve.reject_reason': { ko: '반려 사유 (선택)', en: 'Reject reason (optional)', ja: '却下理由（任意）' },
  'rsv.approve.action_err': { ko: '처리에 실패했습니다.', en: 'Failed to process.', ja: '処理に失敗しました。' },
  'rsv.approve.no_scope': { ko: '담당으로 지정된 예약 자원이 없습니다.', en: 'You are not assigned to any resource.', ja: '担当に指定された予約資源がありません。' },
  'rsv.approve.requester': { ko: '신청자', en: 'Requester', ja: '申請者' },

  // ===== 상태 =====
  'rsv.status.pending': { ko: '승인 대기', en: 'Pending', ja: '承認待ち' },
  'rsv.status.approved': { ko: '확정', en: 'Approved', ja: '確定' },
  'rsv.status.rejected': { ko: '반려', en: 'Rejected', ja: '却下' },
  'rsv.status.cancelled': { ko: '취소', en: 'Cancelled', ja: '取消' },

  // ===== 재설계(2026-08-20) — 주간 타임라인 =====
  'rsv.view.week': { ko: '주', en: 'Week', ja: '週' },
  'rsv.view.month': { ko: '월', en: 'Month', ja: '月' },
  'rsv.week.free': { ko: '{dur} 빔', en: '{dur} free', ja: '{dur} 空き' },
  'rsv.week.free_none': { ko: '남은 시간 없음', en: 'No time left', ja: '空き時間なし' },
  'rsv.week.unavailable': { ko: '예약 불가', en: 'Unavailable', ja: '予約不可' },
  'rsv.week.drag_hint': { ko: '빈 곳을 끌어서 시간을 고르세요', en: 'Drag an empty area to pick a time', ja: '空き部分をドラッグして時間を選びます' },
  'rsv.week.now_label': { ko: '지금 바로', en: 'Free right now', ja: '今すぐ' },
  'rsv.week.now_empty': { ko: '지금 바로 쓸 수 있는 자원이 없습니다', en: 'Nothing is free right now', ja: '今すぐ使える資源がありません' },
  'rsv.week.until_short': { ko: '~{time}', en: 'til {time}', ja: '~{time}' },
  'rsv.week.mine': { ko: '내 예약', en: 'Mine', ja: 'マイ予約' },
  'rsv.week.selected_hint': { ko: '지금 비어 있음', en: 'Currently free', ja: '現在空き' },
  'rsv.week.grid': { ko: '주간 예약 현황', en: 'Weekly availability', ja: '週間の予約状況' },
  'rsv.week.day_events': { ko: '{date} 예약 {n}건', en: '{n} reservations on {date}', ja: '{date} の予約 {n} 件' },

  // ===== 재설계 — 자원 카드의 지금 상태 =====
  'rsv.live.free': { ko: '지금 가능', en: 'Free now', ja: '今すぐ可能' },
  'rsv.live.free_until': { ko: '지금 가능 · {time} 까지', en: 'Free now · until {time}', ja: '今すぐ可能 · {time} まで' },
  'rsv.live.busy': { ko: '사용 중', en: 'In use', ja: '使用中' },
  'rsv.live.busy_until': { ko: '사용 중 · {time} 해제', en: 'In use · free at {time}', ja: '使用中 · {time} に解放' },
  'rsv.live.out': { ko: '운영시간 밖', en: 'Outside hours', ja: '営業時間外' },
  'rsv.live.opens_at': { ko: '{time} 부터', en: 'Opens {time}', ja: '{time} から' },
  'rsv.live.today_free': { ko: '오늘 {dur} 빔', en: '{dur} free today', ja: '今日 {dur} 空き' },
  'rsv.live.left_n': { ko: '{n}개 남음', en: '{n} left', ja: '残り {n}' },
  'rsv.live.unknown': { ko: '현황 확인 중', en: 'Checking…', ja: '状況確認中' },

  // ===== 재설계 — 자원 필터/즐겨찾기 =====
  'rsv.filter.all': { ko: '전체', en: 'All', ja: 'すべて' },
  'rsv.filter.now': { ko: '지금 가능', en: 'Free now', ja: '今すぐ可能' },
  'rsv.filter.instant': { ko: '승인 없음', en: 'No approval', ja: '承認不要' },
  'rsv.fav.group': { ko: '즐겨찾기', en: 'Favorites', ja: 'お気に入り' },
  'rsv.fav.add': { ko: '즐겨찾기 추가', en: 'Add to favorites', ja: 'お気に入りに追加' },
  'rsv.fav.remove': { ko: '즐겨찾기 해제', en: 'Remove from favorites', ja: 'お気に入りを解除' },
  'rsv.resources.none_match': { ko: '조건에 맞는 자원이 없습니다.', en: 'No resource matches.', ja: '条件に合う資源がありません。' },

  // ===== 재설계 — 신청 패널(④) =====
  'rsv.doc.title': { ko: '예약 신청', en: 'New reservation', ja: '予約申請' },
  'rsv.doc.target': { ko: '대상', en: 'Resource', ja: '対象' },
  'rsv.doc.when': { ko: '시간', en: 'When', ja: '時間' },
  'rsv.doc.empty': { ko: '달력에서 빈 시간을 끌어 선택하세요.', en: 'Drag an empty slot on the calendar.', ja: 'カレンダーの空き時間をドラッグして選びます。' },
  'rsv.doc.instant_note': { ko: '승인 없이 바로 확정되는 자원입니다. 신청하면 캘린더에 즉시 올라갑니다.', en: 'This resource is confirmed instantly — no approval needed.', ja: '承認なしで即時確定する資源です。申請すぐにカレンダーへ反映されます。' },
  'rsv.doc.approval_note': { ko: '담당자 승인 후 확정됩니다. 신청 뒤 대기 상태로 남습니다.', en: 'A manager must approve it. It stays pending until then.', ja: '担当者の承認後に確定します。それまでは承認待ちです。' },
  'rsv.doc.slot_unit': { ko: '{n}분 단위로 조절', en: 'Adjust in {n}-minute steps', ja: '{n}分単位で調整' },
  'rsv.doc.clear': { ko: '선택 취소', en: 'Clear', ja: '選択取消' },
  'rsv.doc.submit_hint': { ko: 'Esc 로 선택 취소', en: 'Esc to clear the selection', ja: 'Esc で選択を取り消し' },
  'rsv.doc.qty_max': { ko: '최대 {n}', en: 'max {n}', ja: '最大 {n}' },

  // ===== 재설계 — 나의 예약 =====
  'rsv.mine.count': { ko: '내가 신청한 예약 {n}건', en: '{n} reservations you requested', ja: '申請した予約 {n} 件' },
  'rsv.mine.new': { ko: '새 예약', en: 'New', ja: '新規予約' },
  'rsv.mine.card_next': { ko: '다음 예약', en: 'Next up', ja: '次の予約' },
  'rsv.mine.card_next_in': { ko: '{dur} 뒤', en: 'in {dur}', ja: '{dur} 後' },
  'rsv.mine.card_next_now': { ko: '진행 중', en: 'In progress', ja: '進行中' },
  'rsv.mine.card_next_none': { ko: '없음', en: 'None', ja: 'なし' },
  'rsv.mine.card_pending': { ko: '승인 대기', en: 'Awaiting approval', ja: '承認待ち' },
  'rsv.mine.card_pending_oldest': { ko: '가장 오래된 건 {n}일째', en: 'oldest waiting {n} day(s)', ja: '最も古い件は {n} 日目' },
  'rsv.mine.card_pending_today': { ko: '모두 오늘 신청', en: 'all requested today', ja: 'すべて本日申請' },
  'rsv.mine.card_week': { ko: '이번 주', en: 'This week', ja: '今週' },
  'rsv.mine.card_week_value': { ko: '{n}건 · {dur}', en: '{n} · {dur}', ja: '{n} 件 · {dur}' },
  'rsv.mine.count_n': { ko: '{n}건', en: '{n}', ja: '{n} 件' },
  'rsv.mine.group_today': { ko: '오늘 · {date}', en: 'Today · {date}', ja: '今日 · {date}' },
  'rsv.mine.group_week': { ko: '이번 주', en: 'This week', ja: '今週' },
  'rsv.mine.group_later': { ko: '이후', en: 'Later', ja: 'これ以降' },
  'rsv.mine.group_past': { ko: '지난 예약', en: 'Past', ja: '過去の予約' },
  'rsv.mine.rebook': { ko: '같은 조건으로 예약', en: 'Book again', ja: '同じ条件で予約' },
  'rsv.mine.reapply': { ko: '다시 신청', en: 'Request again', ja: '再申請' },
  'rsv.mine.used': { ko: '사용 완료', en: 'Done', ja: '利用完了' },
  'rsv.mine.section_status': { ko: '상태', en: 'Status', ja: '状態' },
  'rsv.mine.section_period': { ko: '기간', en: 'Period', ja: '期間' },
  'rsv.mine.section_category': { ko: '분류', en: 'Category', ja: '分類' },
  'rsv.mine.period_upcoming': { ko: '다가오는', en: 'Upcoming', ja: 'これから' },
  'rsv.mine.period_past': { ko: '지난', en: 'Past', ja: '過去' },
  'rsv.mine.period_all': { ko: '전체', en: 'All', ja: 'すべて' },
  'rsv.mine.chip_count': { ko: '{label} {n}', en: '{label} {n}', ja: '{label} {n}' },
  'rsv.mine.none_in_view': { ko: '이 조건에 맞는 예약이 없습니다.', en: 'No reservation matches this filter.', ja: 'この条件に合う予約がありません。' },
  'rsv.mine.allday': { ko: '종일', en: 'All day', ja: '終日' },

  // ===== 재설계 — 승인처리 =====
  'rsv.approve.subtitle': { ko: '담당 자원 {n}개 · 대기 {p}건', en: '{n} managed resources · {p} pending', ja: '担当資源 {n} 件 · 承認待ち {p} 件' },
  'rsv.approve.oldest': { ko: '가장 오래된 건 {n}일째', en: 'oldest waiting {n} day(s)', ja: '最も古い件は {n} 日目' },
  'rsv.approve.group_conflict': { ko: '먼저 볼 것 · 시간이 겹칩니다', en: 'Look first · overlapping times', ja: '先に確認 · 時間が重複' },
  'rsv.approve.group_rest': { ko: '나머지 대기', en: 'Other pending', ja: 'その他の承認待ち' },
  'rsv.approve.group_done': { ko: '오늘 처리함', en: 'Handled today', ja: '本日処理済み' },
  'rsv.approve.conflict_note': { ko: '같은 자원의 다른 대기 {n}건과 시간이 겹칩니다 — 함께 승인하면 수량을 넘을 수 있습니다', en: 'Overlaps {n} other pending request(s) on this resource — approving both may exceed capacity', ja: '同じ資源の他の承認待ち {n} 件と時間が重複します — 同時承認は数量超過の恐れがあります' },
  'rsv.approve.selected': { ko: '{n}건 선택됨', en: '{n} selected', ja: '{n} 件選択' },
  'rsv.approve.no_conflict': { ko: '겹치는 건 없음', en: 'no overlap', ja: '重複なし' },
  'rsv.approve.has_conflict': { ko: '겹치는 건이 섞여 있습니다', en: 'selection contains overlaps', ja: '重複する件が含まれます' },
  'rsv.approve.bulk_approve': { ko: '선택 승인', en: 'Approve selected', ja: '選択を承認' },
  'rsv.approve.bulk_reject': { ko: '선택 반려', en: 'Reject selected', ja: '選択を却下' },
  'rsv.approve.bulk_busy': { ko: '처리 중… ({done}/{total})', en: 'Processing… ({done}/{total})', ja: '処理中… ({done}/{total})' },
  'rsv.approve.bulk_partial': { ko: '{ok}건 처리, {fail}건 실패', en: '{ok} done, {fail} failed', ja: '{ok} 件処理・{fail} 件失敗' },
  'rsv.approve.days': { ko: '{n}일째', en: '{n} day(s)', ja: '{n} 日目' },
  'rsv.approve.today_new': { ko: '오늘 신청', en: 'Today', ja: '本日申請' },
  'rsv.approve.reject_title': { ko: '반려 사유', en: 'Reject reason', ja: '却下理由' },
  'rsv.approve.reject_send': { ko: '반려하고 알림', en: 'Reject & notify', ja: '却下して通知' },
  'rsv.approve.reject_note': { ko: '사유는 신청자에게 그대로 전달됩니다', en: 'The reason is sent to the requester as-is', ja: '理由はそのまま申請者へ伝わります' },
  'rsv.approve.reason_assigned': { ko: '이미 배정됨', en: 'Already assigned', ja: '既に割当済み' },
  'rsv.approve.reason_over': { ko: '수량 초과', en: 'Over capacity', ja: '数量超過' },
  'rsv.approve.reason_purpose': { ko: '목적 확인 필요', en: 'Purpose unclear', ja: '目的の確認が必要' },
  'rsv.approve.select_row': { ko: '이 건 선택', en: 'Select this request', ja: 'この件を選択' },
  'rsv.approve.select_all': { ko: '전체 선택', en: 'Select all', ja: 'すべて選択' },
  'rsv.approve.scope_all': { ko: '전체', en: 'All', ja: 'すべて' },
  'rsv.approve.scope_pending_n': { ko: '대기 {n}건', en: '{n} pending', ja: '承認待ち {n} 件' },
  'rsv.approve.scope_none': { ko: '대기 없음', en: 'None pending', ja: '承認待ちなし' },
  'rsv.approve.scope_head': { ko: '담당 자원 {n}', en: 'Managed resources {n}', ja: '担当資源 {n}' },
  'rsv.approve.done_at': { ko: '{label} · {time}', en: '{label} · {time}', ja: '{label} · {time}' },
  'rsv.approve.qty_req': { ko: '{n}개 요청', en: '{n} requested', ja: '{n} 件要求' },
  'rsv.common.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },

  // ===== 재설계 2차(2026-08-20) — 단위별 주/월 뷰 =====
  'rsv.week.pick_range': { ko: '날짜를 끌어 대여 기간을 고르세요', en: 'Drag across days to pick a rental period', ja: '日付をドラッグして貸出期間を選びます' },
  'rsv.week.range_note': { ko: '같은 날 {n}대까지 · 반납일 포함', en: 'up to {n} at a time · return day included', ja: '同日 {n} 台まで · 返却日を含む' },
  'rsv.week.left_of': { ko: '{n}/{total}', en: '{n}/{total}', ja: '{n}/{total}' },
  'rsv.month.tap_day': { ko: '날짜를 누르면 그 주로 넘어가 시간을 고릅니다', en: 'Click a date to jump to that week', ja: '日付を押すとその週へ移動します' },
  'rsv.month.summary': { ko: '이 달 · 예약 {n}건', en: 'This month · {n} reservations', ja: '今月 · 予約 {n} 件' },
  'rsv.month.more': { ko: '+{n}건', en: '+{n}', ja: '+{n} 件' },
  'rsv.month.grid': { ko: '월간 예약 현황', en: 'Monthly availability', ja: '月間の予約状況' },
  'rsv.week.lanes': { ko: '주간 대여 현황', en: 'Weekly rentals', ja: '週間の貸出状況' },
  'rsv.week.picked_range': { ko: '{range} · {dur}', en: '{range} · {dur}', ja: '{range} · {dur}' },
  'rsv.doc.pick_first': { ko: '먼저 달력에서 시간을 고르세요', en: 'Pick a time on the calendar first', ja: 'まずカレンダーで時間を選んでください' },

  'rsv.mine.reset': { ko: '필터 초기화', en: 'Reset filters', ja: 'フィルター初期化' },
  'rsv.mine.period_unlocked': { ko: '{label} 는 지난 목록에만 있어 기간을 “전체”로 바꿨습니다', en: '{label} only exists in past items — period switched to “All”', ja: '{label} は過去の一覧にのみあるため、期間を「すべて」に変えました' },
  'rsv.mine.scope_label': { ko: '{status} · {period} — {n}건', en: '{status} · {period} — {n}', ja: '{status} · {period} — {n} 件' },
  'rsv.approve.conflict_win': { ko: '같은 자원의 다른 대기와 {from}–{to} 이 겹칩니다', en: 'Overlaps another pending request on this resource, {from}–{to}', ja: '同じ資源の他の承認待ちと {from}–{to} が重複します' },

  // ===== 재설계 — 기간 표기 =====
  'rsv.dur.d': { ko: '{n}일', en: '{n}d', ja: '{n}日' },
  'rsv.dur.h': { ko: '{n}시간', en: '{n}h', ja: '{n}時間' },
  'rsv.dur.m': { ko: '{n}분', en: '{n}m', ja: '{n}分' },
  'rsv.dur.hm': { ko: '{h}시간 {m}분', en: '{h}h {m}m', ja: '{h}時間{m}分' },
} as const satisfies Record<string, Record<Lang, string>>;

export type ReservationI18nKey = keyof typeof MESSAGES;

/** fallback chain (lang → en → ko) + {param} 치환 — project-i18n 동형. */
export function tRsv(key: ReservationI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key] as Record<Lang, string> | undefined;
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

/**
 * 분 → "1시간 30분" / "45분" / "2일" (언어별). 0 이하면 빈 문자열.
 * 쪼개기는 순수 함수(`reservation-week.splitDuration`)가 하고 여기서는 문구만 붙인다.
 */
export function tRsvDuration(totalMinutes: number): string {
  if (totalMinutes <= 0) return '';
  const day = 24 * 60;
  if (totalMinutes >= day && totalMinutes % day === 0) return tRsv('rsv.dur.d', { n: totalMinutes / day });
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return tRsv('rsv.dur.m', { n: m });
  if (m === 0) return tRsv('rsv.dur.h', { n: h });
  return tRsv('rsv.dur.hm', { h, m });
}

/** 예약 상태 → 표시 라벨. */
export function tRsvStatus(status: string): string {
  switch (status) {
    case 'pending': return tRsv('rsv.status.pending');
    case 'approved': return tRsv('rsv.status.approved');
    case 'rejected': return tRsv('rsv.status.rejected');
    case 'cancelled': return tRsv('rsv.status.cancelled');
    default: return status;
  }
}

// base-i18n thin wrapper (cycle 41 통합 패턴 일관)
export function getRsvLang(): Lang { return getSharedLang(); }
export function setRsvLang(value: Lang): void { setSharedLang(value); }
export function subscribeRsvLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}

// 테스트 전수 가드용 export (i18n 누락 키 런타임 은닉 방어)
export const RESERVATION_I18N_MESSAGES = MESSAGES;
